/**
 * The regulatory panels' pure half (13-27): what the reportability panel shows for each answer, the plain checks
 * against the incident report, the deadline as the form stands, and the notification panel's groups and due lines.
 * `components/record/panels/ReportabilityPanel.tsx` and `NotificationPanel.tsx` render it; the 13-20 engine decides
 * every save (`buildRecordVerb`), and `lib/notificationDeadlines` every deadline.
 *
 * Nothing here retypes a column list, a choice or a rule. The detail columns follow their own field-set rules (the
 * SIRS columns are required "when `ReportabilityDecision` = SIRS-P1 / SIRS-P2", so they show then); the notice
 * groups are the notification verb's list (13-20's allow-list), split by 13-22's notice prefixes; every value a check
 * names goes through `liveChoice`. Pure: no React, no Graph, no `Date.now()`.
 */

import { liveChoice } from '../config/fieldset/live';
import { fieldOf, verbColumns } from '../config/fieldset/registerFields';
import type { RecordRow, RequiredRule } from '../types/records';
import { melbourneWallToInstant } from './fieldValues';
import {
  NOT_REPORTABLE_DECISION,
  PENDING_DECISION,
  noticeDeadlines,
  notificationDue,
  type DeadlineRule,
  type DueResult,
  type Regime,
} from './notificationDeadlines';
import { NOTICE_COLUMNS, SAH_DELEGATES_LINE, isAgedCareRecord, type NoticeKind } from './recordAuthority';
import { timeKeyOf, verbInputColumns } from './verbForm';

const I = 'incidents';

/** Each regulator in words, for the panels. */
export const REGIME_WORDS: Readonly<Record<Regime, string>> = {
  ndis: 'NDIS Commission',
  sirs: 'SIRS (Aged Care Quality and Safety Commission)',
  worksafe: 'WorkSafe Victoria',
  whs: 'Workplace injury (WIRC Act)',
  other: 'Other',
};

export const DECISION = 'ReportabilityDecision';
export const AWARE_AT = 'AwareAt';
export const DUE_AT = 'NotificationDueAt';

/** The decisions a column's own triage rule names (`{ when: ReportabilityDecision, in: [...] }`). */
function decisionsOf(column: string): readonly string[] {
  const rule = fieldOf(I, column)?.required.triage as RequiredRule | undefined;
  if (typeof rule !== 'object' || rule.when !== DECISION || !('in' in rule)) {
    throw new Error(`regulatoryPanels: ${column} is not required by a ReportabilityDecision value`);
  }
  return rule.in;
}

/** SIRS-P1 / SIRS-P2, read from `SirsKind`'s rule; NDIS-Reportable, from `NdisRiKind`'s. */
export const SIRS_DECISIONS: readonly string[] = decisionsOf('SirsKind');
export const NDIS_DECISIONS: readonly string[] = decisionsOf('NdisRiKind');
const [WORKSAFE_DECISION] = liveChoice(I, DECISION, 'WorkSafe');
const [WSN_YES] = liveChoice(I, 'WorkSafeNotifiable', 'Yes');

// ---------------------------------------------------------------------------
// The reportability panel
// ---------------------------------------------------------------------------

/** The reportability verb's inputs, decision first and then `AwareAt` (13-20's list; `NotificationDueAt` is computed). */
export function reportabilityInputs(): string[] {
  const own = verbInputColumns(I, 'reportability');
  return [DECISION, AWARE_AT, ...own.filter((c) => c !== DECISION && c !== AWARE_AT)];
}

/**
 * Whether a reportability column shows for the chosen answer: the decision and `AwareAt` always; a `Sirs…` column
 * for a SIRS decision, an `Ndis…` column for an NDIS one (the plan's conditional detail, keyed by the columns' own
 * field-set rules and FIELDSET's name prefixes).
 */
export function reportabilityShows(column: string, decision: string): boolean {
  if (column.startsWith('Sirs')) return SIRS_DECISIONS.includes(decision);
  if (column.startsWith('Ndis')) return NDIS_DECISIONS.includes(decision);
  return true;
}

/**
 * Who records the reportability call on this record (D-13-L-2), in role words (no staff name, D-13-17-5): on an
 * aged-care or mixed-unit record the SaH decider, then the delegates in order (D-13-33).
 */
export function decidesThisOne(row: RecordRow): string {
  return isAgedCareRecord(row)
    ? ['The SaH reportability decider decides this one.', SAH_DELEGATES_LINE].filter(Boolean).join(' ')
    : 'Oversight decides this one.';
}

const [NO_HARM] = liveChoice(I, 'HarmKind', 'Near miss (no harm)');
const [ALLEGED] = liveChoice(I, 'IncidentBasis', 'Alleged');
const [SIRS_P1] = liveChoice(I, DECISION, 'SIRS-P1');

/**
 * The plain checks against the incident report (the D-13-10-3 pattern): a line asking the decider to confirm, never a
 * block and never a suggestion of the answer.
 */
export function reportPrompts(row: RecordRow, decision: string): string[] {
  const out: string[] = [];
  if (row.fields.HarmKind === NO_HARM && decision === SIRS_P1) {
    out.push(`The report says nobody was harmed (“${NO_HARM}”). Please confirm SIRS Priority 1 is the decision you mean.`);
  }
  if (row.fields.IncidentBasis === ALLEGED && decision === NOT_REPORTABLE_DECISION) {
    out.push(`The report says this is alleged (“${ALLEGED}”). Please confirm Not reportable is the decision you mean.`);
  }
  return out;
}

/** The form's `AwareAt` as an instant (the inputs' Melbourne day and time), else the record's. */
function awareOf(row: RecordRow, values: Readonly<Record<string, string>>): string | null {
  const day = values[AWARE_AT] ?? '';
  const time = values[timeKeyOf(AWARE_AT)] ?? '';
  if (day && time) {
    const r = melbourneWallToInstant(day, time);
    return r.ok ? r.value : null;
  }
  if (!day && !time) return typeof row.fields[AWARE_AT] === 'string' ? (row.fields[AWARE_AT] as string) : null;
  return null;
}

/** The deadline as the form stands (decision, `AwareAt`, class), with the WHS verb's answer from the record. */
export function formDue(row: RecordRow, values: Readonly<Record<string, string>>): DueResult {
  const text = (c: string) => (typeof values[c] === 'string' && values[c] !== '' ? values[c] : ((row.fields[c] as string | null) ?? null));
  return notificationDue({
    awareAt: awareOf(row, values),
    decision: values[DECISION] ?? '',
    ndisNoticeClass: text('NdisNoticeClass'),
    sirsKind: text('SirsKind'),
    workSafeNotifiable: (row.fields.WorkSafeNotifiable as string | null) ?? null,
  });
}

/** What happens to the stored deadline for the chosen decision, in words (D-13-27-3, D-13-27-13). */
export function dueOutcome(row: RecordRow, decision: string, due: DueResult): 'set' | 'keep' | 'clear' | 'none' | 'missing' {
  const stored = typeof row.fields[DUE_AT] === 'string' && row.fields[DUE_AT] !== '';
  if (due.dueAt !== null) return 'set';
  if (due.missing) return 'missing';
  if (decision === PENDING_DECISION) return stored ? 'keep' : 'none';
  if (decision === NOT_REPORTABLE_DECISION) return stored ? 'clear' : 'none';
  return 'none';
}

// ---------------------------------------------------------------------------
// The notification panel
// ---------------------------------------------------------------------------

/**
 * Shared columns that belong to the NDIS notice only: `InfoWithheld` records reliance on IMRI s 22 (OB-NDIS-63),
 * which has no SIRS equivalent.
 */
const NDIS_ONLY_SHARED: readonly string[] = ['InfoWithheld'];

/** The notification verb's list (13-20's allow-list, 19 columns). */
export const NOTIFICATION_COLUMNS: readonly string[] = verbColumns(I, 'notification');

const SHARED = NOTIFICATION_COLUMNS.filter((c) => !Object.values(NOTICE_COLUMNS).some((cols) => cols.includes(c)));

/**
 * The columns each group shows, in the plan's order; every one is in the allow-list (asserted at load). NDIS: its
 * notices, then the shared columns. SIRS: its notices, then the shared columns but the NDIS-only one. WorkSafe is
 * HR & WHS's own panel (13-28).
 */
export const NOTICE_GROUPS: Readonly<Record<Exclude<NoticeKind, 'worksafe'>, readonly string[]>> = {
  ndis: [
    'NdisInitialNoticeAt',
    'NdisInitialNoticeMethod',
    'NdisFurtherNoticeAt',
    'FurtherActionsProposed',
    'InfoWithheld',
    'RegulatorReference',
    'FinalReportRequired',
    'FinalReportDueOn',
    'FinalReportGivenAt',
    'NotifierUPN',
    'NotifierPosition',
    'ExternalOutcome',
  ],
  sirs: [
    'SirsNoticeMethod',
    'SirsNoticeAt',
    'SirsAdditionalNoticeAt',
    'FurtherActionsProposed',
    'RegulatorReference',
    'FinalReportRequired',
    'FinalReportDueOn',
    'FinalReportGivenAt',
    'NotifierUPN',
    'NotifierPosition',
    'ExternalOutcome',
  ],
};
for (const [kind, cols] of Object.entries(NOTICE_GROUPS) as [Exclude<NoticeKind, 'worksafe'>, readonly string[]][]) {
  const expected = [...NOTICE_COLUMNS[kind], ...SHARED.filter((c) => kind === 'ndis' || !NDIS_ONLY_SHARED.includes(c))];
  if (cols.length !== expected.length || !expected.every((c) => cols.includes(c))) {
    throw new Error(`regulatoryPanels: the ${kind} group is not the allow-list's ${kind} and shared columns`);
  }
}

/** The notices this record's decision calls for (WorkSafe also when the WHS verb answered Yes). */
export function noticesFor(row: RecordRow): NoticeKind[] {
  const decision = typeof row.fields[DECISION] === 'string' ? (row.fields[DECISION] as string) : '';
  const out: NoticeKind[] = [];
  if (NDIS_DECISIONS.includes(decision)) out.push('ndis');
  if (SIRS_DECISIONS.includes(decision)) out.push('sirs');
  if (decision === WORKSAFE_DECISION || row.fields.WorkSafeNotifiable === WSN_YES) out.push('worksafe');
  return out;
}

/** The notification panel shows only once a call has been made that has a notice (the plan). */
export function isNotifiable(row: RecordRow): boolean {
  return noticesFor(row).length > 0;
}

/** Each notice's due rule for this record (from its stored decision, `AwareAt`, class and WorkSafe answer). */
export function noticeRules(row: RecordRow): DeadlineRule[] {
  const f = row.fields;
  const str = (c: string) => (typeof f[c] === 'string' ? (f[c] as string) : null);
  return noticeDeadlines({
    awareAt: str(AWARE_AT),
    decision: str(DECISION),
    ndisNoticeClass: str('NdisNoticeClass'),
    workSafeNotifiable: str('WorkSafeNotifiable'),
    notices: f,
  });
}

/** Due versus done for one notice column: amber (never red, D-13-15-4) once past due while its column is blank. */
export interface NoticeState {
  rule: DeadlineRule;
  given: string | null;
  overdue: boolean;
}

export function noticeState(rule: DeadlineRule, row: RecordRow, typed: string, now: Date): NoticeState {
  const stored = rule.notice && typeof row.fields[rule.notice] === 'string' && row.fields[rule.notice] !== '' ? (row.fields[rule.notice] as string) : null;
  const blank = stored === null && typed.trim() === '';
  return { rule, given: stored, overdue: blank && !rule.conditional && Date.parse(rule.dueAt) < now.getTime() };
}
